import { useRef, useState } from "react";
import { Pause, Play, PlayCircle, Square } from "lucide-react";
import { DEMOS, runDemo } from "../../lab/demoPlayer";

/** Simulator: « Démo » menu + caption bar. Plays the real flow in the phone, slowly, with explanations. */
const DemoControls = ({ play }) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const control = useRef(null);

  const start = async (demo) => {
    setOpen(false);
    const steps = demo.steps(play);
    control.current = { stopped: false, paused: false, speed: 1, onStep: (index, step) => setState({ title: demo.title, index, total: steps.length, step, paused: control.current.paused }) };
    await runDemo(steps, control.current);
    if (!control.current.stopped) setTimeout(() => setState(null), 2500);
  };

  const toggle = () => {
    control.current.paused = !control.current.paused;
    setState((current) => ({ ...current, paused: control.current.paused }));
  };

  const stop = () => {
    control.current.stopped = true;
    setState(null);
  };

  return (
    <>
      <div className="relative">
        <button type="button" onClick={() => setOpen((value) => !value)} className="inline-flex items-center gap-1.5 rounded-full bg-[#122118] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0a2914]" data-testid="lab-demo">
          <PlayCircle className="h-3.5 w-3.5" />Démo
        </button>
        {open && (
          <div className="fixed inset-x-2 top-28 z-40 sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 mt-2 sm:w-64 rounded-xl border border-gray-100 bg-white p-2 shadow-lg" data-testid="lab-demo-menu">
            {DEMOS.map((demo) => (
              <button key={demo.flowId} type="button" onClick={() => start(demo)} className="block w-full rounded-lg px-3 py-2 text-left text-xs font-semibold text-[#122118] hover:bg-[#F3FCF7]" data-testid={`lab-demo-flow-${demo.flowId}`}>
                {demo.title}
              </button>
            ))}
            <p className="px-3 py-2 text-[11px] text-gray-500">Les autres flows et la boucle complète arrivent après votre validation du Flow 1.</p>
          </div>
        )}
      </div>
      {state && (
        <div className="fixed bottom-6 left-1/2 z-[60] w-[min(42rem,92vw)] -translate-x-1/2 rounded-2xl bg-[#0a2914]/95 px-5 py-4 text-white shadow-2xl" data-testid="lab-demo-caption">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#7be3a3]">{state.title} · étape {state.index + 1}/{state.total}</span>
            <div className="ml-auto flex gap-1.5">
              <button type="button" onClick={toggle} className="rounded-full bg-white/10 p-1.5 hover:bg-white/20" aria-label={state.paused ? "Reprendre" : "Pause"} data-testid="lab-demo-pause">
                {state.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              </button>
              <button type="button" onClick={stop} className="rounded-full bg-white/10 p-1.5 hover:bg-white/20" aria-label="Arrêter" data-testid="lab-demo-stop"><Square className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          <p className="mt-1 text-lg font-semibold">{state.step.caption}</p>
          {state.step.sub && <p className="mt-0.5 text-sm text-white/80">{state.step.sub}</p>}
          <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/15">
            <div className="h-full bg-[#25D366] transition-[width] duration-500" style={{ width: `${((state.index + 1) / state.total) * 100}%` }} />
          </div>
        </div>
      )}
    </>
  );
};

export default DemoControls;
